import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import {
	AspectRatio,
	Avatar,
	BookmarkOutline,
	Box,
	Center,
	ChatOutline,
	Display,
	DotsHorizontalOutline,
	Flex,
	Hide,
	HStack,
	IconButton,
	Image,
	PlusCircleOutline,
	rem,
	Show,
	Spacer,
	Stack,
	Text,
	ThumbUpOutline,
	useBreakpointValue,
} from '@bonusly/design-system';
import { useCurrentCompany } from '@bonusly/who-am-i';

import { TextPreview } from '../../components/bonus/text-preview';
import { getCelebrationStyles } from '../../components/celebrations/utils';
import { Reveal } from '../../components/display/reveal';
import { ActionButton } from '../../components/recognition/bonus/components/bonus/action-button';
import { CelebrationType } from '../../entities/celebrations';

type FeedPreviewProps = {
	amount: number | null;
	headline: string;
	iconURL: string | null;
	imageURL: string | null;
	reason: string;
	type: CelebrationType;
	userImage: string;
	userDisplayName: string;
	username: string;
};

export const FeedPreview: FC<FeedPreviewProps> = ({ headline, reason, amount, iconURL, type, imageURL, username, userImage, userDisplayName }) => {
	const company = useCurrentCompany();
	const { t } = useTranslation(['celebrations', 'common']);
	const { bgColor, imageComponent, iconComponent } = getCelebrationStyles(type);
	const ratio = useBreakpointValue({ base: 4 / 3, sm: 1 });

	// Replace the template with example values, which will then be properly formatted in the ReasonPreview component
	const interpolatedReason = (() => {
		// Only replace the first instances of recipient and amount, but replace all instances of company_name
		let text = reason.replace('@recipient', `@${username}`);
		text = amount ? text.replace('+amount', `+${amount}`) : text.replace('+amount', '+100');
		text = text.replaceAll('company_name', company.name);

		// anniversary_date is only interpolated for work anniversaries and birthdays
		if (type === CelebrationType.WorkAnniversary || type === CelebrationType.Birthday) {
			text = text.replaceAll(
				'anniversary_date',
				t('common:intl_datetime', {
					value: new Date(),
					formatParams: {
						value: {
							day: 'numeric',
							month: 'long',
						},
					},
				})
			);
		}

		// So, for the `nth` interpolation we'll check for either a preceding space or the start of the string. Otherwise we might end up
		// with the word "Seventeenth" turning into "Seventee1st"
		if (type === CelebrationType.WorkAnniversary) {
			text = text.replace(/^nth/, t('celebrations:preview.nth')).replaceAll(/ nth/g, ` ${t('celebrations:preview.nth')}`);
		}

		return text;
	})();

	return (
		<Box bgColor="white" borderRadius="lg" mb={4} border="1px solid" borderColor="border.default">
			{/* Header */}
			<HStack p={4} columnGap={2} borderBottom="1px solid" borderColor="border.default">
				{iconURL && <Image src={iconURL} boxSize={rem(38)} borderRadius="full" />}
				{!iconURL && iconComponent && (
					<Box bgColor={bgColor} boxSize={rem(38)} borderRadius="full" overflow="hidden">
						{iconComponent}
					</Box>
				)}
				{iconURL && !iconComponent && <Box bgColor="primary.bgAlpha" boxSize={rem(38)} borderRadius="full" />}
				<Reveal if={!!amount}>
					<Center bgColor="points.bgAlpha" h={rem(38)} borderRadius="full" color="points.fg" fontWeight="semibold" px={2.5} fontSize="lg">
						+{amount ? Math.trunc(Math.abs(amount)) : 'amount'}
					</Center>
				</Reveal>
				<Spacer />
				<Text size="sm" color="gray.700">
					{t('celebrations:preview.timeStamp')}
				</Text>
				<BookmarkOutline color="secondary.fg" />
				<DotsHorizontalOutline color="secondary.fg" />
			</HStack>

			{/* Body */}
			<Flex bgColor={imageURL ? 'white' : bgColor} flexDir={{ base: 'column-reverse', sm: 'row' }}>
				<Stack p={4} minH={{ base: 0, sm: rem(230) }} justify="center">
					<Avatar src={userImage} name={userDisplayName} size="xl" />
					<Display mb={2} size="2xs">
						{headline.trim() || t('celebrations:preview.headline')}
					</Display>
					<TextPreview text={interpolatedReason.trim() || t('celebrations:preview.message')} />
				</Stack>
				<Spacer />

				{imageURL && (
					<AspectRatio w="full" maxW={{ sm: '40%' }} ratio={ratio}>
						<Image src={imageURL} objectFit="cover" w="full" h="full" />
					</AspectRatio>
				)}

				{!imageURL && imageComponent && <Center>{imageComponent}</Center>}
			</Flex>

			{/* Footer */}
			<HStack px={5} py={4} spacing={2} borderTop="1px solid" borderColor="border.default">
				<ActionButton icon={<ChatOutline />} color="gray">
					{t('celebrations:preview.comment')}
				</ActionButton>
				<Hide below="xs">
					<ActionButton icon={<PlusCircleOutline />} color="green">
						{t('celebrations:preview.addOn')}
					</ActionButton>
				</Hide>
				<Show below="xs">
					<IconButton aria-label="Like" bg="white" border="1px solid" borderColor="gray.100" _hover={{ bg: 'grayAlpha.100' }}>
						<DotsHorizontalOutline />
					</IconButton>
				</Show>
				<Spacer />

				<Hide below="xs">
					<ActionButton icon={<ThumbUpOutline />} color="blue">
						{t('celebrations:preview.like')}
					</ActionButton>
				</Hide>
				<Show below="xs">
					<IconButton aria-label="Like" bg="white" border="1px solid" borderColor="gray.100" _hover={{ bg: 'blueAlpha.100' }}>
						<ThumbUpOutline />
					</IconButton>
				</Show>
			</HStack>
		</Box>
	);
};
